import { Box, CircularProgress, Menu, MenuItem } from '@mui/material';
import { IconButton } from 'design-system';
import { ButtonView as Button } from 'interfaces/graphqlViews';
import { useState } from 'react';
import { MdMoreVert } from 'react-icons/md';
import useIsDarkMode from 'hooks/useIsDarkMode';
import { ButtonColor, ButtonIcon, ButtonVariant } from 'generated/graphql';
import ActionButton, { ActionButtonProps } from './ActionButton';
import ActionList from './ActionList';
import BtnIcon from './BtnIcon';

export interface KebabMenuOption {
  startIcon: ButtonIcon;
  value: string;
  button?: Button; // TODO: Move to this. Drop startIcon and value.
  onClick?: () => void;
  isLoading?: boolean;
}

const KebabMenu = ({ options, actions }: { options?: KebabMenuOption[]; actions?: ActionButtonProps[] }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMenuButton, setSelectedMenuButton] = useState<Button | undefined>(undefined);
  const isDarkMode = useIsDarkMode();

  const handleKebabIconClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleKebabMenuClose = () => {
    setAnchorEl(null);
  };

  if (!options?.length && !actions?.length) return null;

  return (
    <>
      {/* Kebab Icon: Opens the Kebab Menu */}
      <IconButton
        track={false}
        aria-controls="simple-menu"
        aria-haspopup="true"
        sx={(theme) => ({
          backgroundColor: theme.palette.tertiary.main,
          color: theme.palette.primary.main,
          '&:hover': {
            backgroundColor: theme.palette.tertiary.dark,
          },
          [theme.breakpoints.down('md')]: {
            backgroundColor: 'transparent',
            color: isDarkMode ? theme.palette.primary.contrastText : theme.palette.primary.main,
            '&:hover': {
              backgroundColor: theme.palette.tertiary.main,
            },
          },
        })}
        onClick={handleKebabIconClick}
      >
        <MdMoreVert />
      </IconButton>

      {/* Kebab Menu: Displays the options */}
      {/* Kept mounted once closed: forms open through FormDialogProvider, but an
          ActionButton renders its action list, table dialog, payment details and
          download overlay itself, and unmounting the menu would take them with it. */}
      <Menu
        id="kebabMenu"
        keepMounted
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleKebabMenuClose}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              border: `1px solid ${theme.palette.grey[200]}`,
              boxShadow: theme.shadows[2],
            }),
          },
        }}
      >
        {options?.map((option, index) => {
          const hasMultipleActions = option.button && option.button.actions.length > 1;
          const isOptionLoading = option.isLoading ?? false;

          return (
            <MenuItem
              key={`${option.value}-${index}`}
              disabled={isOptionLoading}
              onClick={hasMultipleActions ? () => setSelectedMenuButton(option.button) : option.onClick}
              sx={{ width: 170 }}
            >
              <Box
                sx={(theme) => ({
                  paddingRight: theme.spacing(1),
                })}
              >
                {isOptionLoading ? <CircularProgress size={20} /> : <BtnIcon icon={option.startIcon} />}
              </Box>
              {option.value}
            </MenuItem>
          );
        })}
        {actions?.map((action, index) => (
          // Closes in the capture phase, since ActionButton stops the click
          // from bubbling.
          <Box key={`${action.value}-${index}`} onClickCapture={action.disabled ? undefined : handleKebabMenuClose}>
            <ActionButton
              {...action}
              color={ButtonColor.Primary}
              variant={ButtonVariant.Text}
              size="medium"
              fullWidth
              sx={(theme) => ({
                justifyContent: 'flex-start',
                minWidth: 170,
                paddingX: 2,
                borderRadius: 0,
                color: theme.palette.text.primary,
              })}
            />
          </Box>
        ))}
      </Menu>

      {/* Action List: Displays the actions for the selected kebab menu item that has multiple actions */}
      {selectedMenuButton && (
        <ActionList open={!!selectedMenuButton} button={selectedMenuButton} onClose={handleKebabMenuClose} />
      )}
    </>
  );
};

export default KebabMenu;
